import { getStaticMediaPathCreator } from 'utils/getStaticMediaPathCreator';
import { createFortifyCaseStudyUITemplate } from '../ui-templates';
import { CASE_STUDIES } from 'features/Home/CaseStudies/constants';

const mediaPathCreator = getStaticMediaPathCreator(CASE_STUDIES.FORTIFY);

export const FORTIFY_CASE_STUDY_UI_CONFIG = createFortifyCaseStudyUITemplate({
  titleBannerProperties: {
    caseStudyTitle: 'Fortify: Custom Industrial IoT Platform',
    description: 'Near Real-Time Dock Equipment Monitoring',
    bannerImageUrl: mediaPathCreator('fortify-banner-v1.png'),
    companyLogoUrl: mediaPathCreator('fortify-logo-v1.png'),
    projectName: 'Fortify',
  },
  introductionProperties: {
    title: 'How C4Workx Built an IoT Asset Monitoring Platform',
    paragraphs: [
      'Warehouses and industrial facilities operate with near-zero visibility into the health of their dock equipment. Without connected oversight, asset failures go undetected until costly downtime forces an emergency service call, a pattern that disrupts operations and inflates maintenance budgets.',
      'Fortify came to C4Workx with an ambitious goal: Introduce IoT monitoring into dock and door operations for the first time, transforming how facility managers track, maintain, and optimize loading dock assets at scale.',
      'C4Workx delivered a fully custom, end-to-end IoT software platform spanning field survey tooling, sensor installation, and a real-time command center dashboard, all grounded in deep user research and built around how warehouse operations actually work.',
    ],
    tagGroups: [
      { heading: 'Industry', items: ['Industrial IoT'] },
      { heading: 'Services', items: ['Custom Software Development', 'IoT Consulting', 'Strategic Advisory'] },
      { heading: 'Technology', items: ['React', 'Azure Services', 'DataBricks', 'AzureIOT'] },
    ],
    mediaUrl: mediaPathCreator('fortify-introduction-v1.png'),
  },
  businessChallengeProperties: {
    text: 'How do you give every warehouse operator the power to turn live sensor data into real-time, intelligent decisions?',
    highlightedPhrases: ['every warehouse operator', 'live sensor data', 'real-time, intelligent decisions'],
  },
  metricCardsSectionProperties: {
    sectionTitle: 'Impact at a Glance',
    metrics: [
      {
        iconName: 'clipboard',
        label: 'Facilities Surveyed',
        value: '1,009',
      },
      {
        iconName: 'facility',
        label: 'Fortified Facilities',
        value: '852',
      },
      {
        iconName: 'sensor',
        label: 'Sensors Installed',
        value: '12,410',
      },
      {
        iconName: 'door',
        label: 'Doors Monitored',
        value: '4,572',
      },
      {
        iconName: 'leveler',
        label: 'Levelers Monitored',
        value: '1,519',
      },
    ],
  },
  productCardsSectionProperties: {
    sectionTitle: 'One Connected System',
    introductionText:
      'A purpose-built tablet application that enables field technicians to conduct accurate, standardized site surveys of dock equipment at any facility, online or offline.',
    products: [
      {
        title: 'Capture',
        subtitle: 'Surveyor',
        description: 'Capture asset details',
        mediaSrc: mediaPathCreator('graphic-section-card-media-1-v1.png'),
      },
      {
        title: 'Activate',
        subtitle: 'Installer',
        description: 'Connect sensors to assets',
        mediaSrc: mediaPathCreator('graphic-section-card-media-2-v1.png'),
      },
      {
        title: 'Operate',
        subtitle: 'Command Center',
        description: 'Continuously monitor asset health',
        mediaSrc: mediaPathCreator('graphic-section-card-media-3-v1.png'),
      },
    ],
  },
  solutionsSectionProperties: {
    sectionTitle: 'The Problems We Solved',
    introductionText: `Fortify was entering uncharted territory. With no existing blueprint for connected dock operations and no
    comparable platform to reference, building this system meant solving problems that had never been solved
    before. C4Workx approached this by embedding deeply into Fortify's operational reality, conducting field
    research across facilities to understand exactly how teams worked and where the biggest gaps existed. That
    research surfaced five foundational challenges that shaped every product and engineering decision that
    followed.`,
    solutions: [
      {
        heading: 'Visibility',
        problemText: 'Facilities had no reliable record of their assets or the condition they were in',
        solutionText:
          'A standardized asset tracking system that provides a trusted, real-time record of every dock asset and its current condition',
      },
      {
        heading: 'Data Quality',
        problemText: 'Scattered manual data made it impossible to make reliable operational decisions',
        solutionText:
          'Structured digital capture with built-in validation, ensuring every asset record is accurate, consistent and audit-ready',
      },
      {
        heading: 'Standardization',
        problemText:
          'The sheer variety of asset types and configurations made consistent data capture and analysis nearly impossible',
        solutionText:
          'A flexible IoT platform built to handle variation while enforcing standardized, structured data output',
      },
      {
        heading: 'Monitoring',
        problemText: 'No alerts, no trend data, and no early warning system for at-risk equipment',
        solutionText:
          'A digital twin engine that maps live sensor telemetry to individual assets, enabling predictive maintenance and automated condition alerts',
      },
      {
        heading: 'Centralization',
        problemText: 'Operational planning relied on outdated survey data disconnected from real-time field conditions',
        solutionText:
          'A unified web platform that aggregates all  data into one place, giving facility operators a complete operational picture to make confident, informed decisions.',
      },
    ],
  },
  accordionsSectionProperties: {
    sectionTitle: 'Custom IoT Platform',
    firstAccordion: {
      headingTitle: 'SURVEYOR - Tablet Application',
      headingSubtitle: 'Capture',
      headingDescription: 'Field Asset Capture Application',
      contentBulletPoints: [
        'Dynamic, rule-based surveys that enforce required fields and validate data at the point of entry',
        'Embedded industry knowledge that guides technicians through correct capture workflows',
        'Instant survey report generation with near real-time client access',
        'Advanced positional awareness and floor plan contouring',
        'Multi-technician coordination for large-scale deployments',
        'Fully offline-capable, no connectivity required in the field',
        'Fully secured tablet application',
      ],
      contentIntroductionText:
        'A purpose-built tablet application that enables field technicians to conduct accurate, standardized site surveys of dock equipment at any facility, online or offline.',
      contentSubtitle: 'Field survey, done right',
      contentResultText:
        'Faster surveys, fewer data errors, and consistent structured records that eliminate downstream rework and integration issues - cutting survey cycle time significantly.',
      contentMedia: {
        src: mediaPathCreator('solution-overview-media-1-v1.mp4'),
        type: 'video',
        side: 'left',
        loop: true,
      },
    },
    secondAccordion: {
      headingTitle: 'INSTALLER - Mobile Application',
      headingSubtitle: 'Activate',
      headingDescription: 'IoT Deployment & Sensor Onboarding App',
      contentBulletPoints: [
        'Guided installation workflows that reduce technician error and training time',
        'Digital twin linking, each physical asset is paired to its live sensor counterpart in near real time',
        'Offline Bluetooth setup for environments with no cellular or Wi-Fi access',
        'Robust mobile-to-cloud synchronization ensuring no data is lost between field and platform',
        'Real-time field validation of sensor connectivity before technician departure',
        'Built-in asset and inventory management for end-to-end deployment tracking',
        'Native Microsoft Dynamics 365 (D365) integration for inventory control and quality assurance',
      ],
      contentIntroductionText:
        'A mobile application built for the speed and precision of IoT hardware installation, covering sensor-to-cloud setup, inventory management, and real-time connectivity validation.',
      contentSubtitle: 'IoT deployment pipeline',
      contentResultText:
        'A controlled, auditable IoT deployment pipeline that guarantees every sensor is correctly configured, connected, and trackable - eliminating ghost assets and post-install correction visits.',
      contentMedia: {
        src: mediaPathCreator('solution-overview-media-2-v1.mp4'),
        type: 'video',
        side: 'right',
      },
      contentDiagramSteps: [
        {
          type: 'entity',
          iconName: 'mobile',
          title: 'Mobile',
          subtitle: 'Install & configure',
        },
        {
          type: 'connection',
          title: 'Bluetooth',
        },
        {
          type: 'entity',
          iconName: 'sensor',
          title: 'Sensor',
          subtitle: 'Monitor asset activity',
        },
        { type: 'connection', title: 'LoRaWAN' },
        {
          type: 'entity',
          iconName: 'gateway',
          title: 'Gateway',
          subtitle: 'Sensor to cloud proxy',
        },
        {
          type: 'connection',
          title: 'Cellular',
        },
        {
          type: 'entity',
          iconName: 'cloud',
          title: 'Cloud',
          subtitle: 'Powers the HUD',
        },
      ],
    },
    thirdAccordion: {
      headingTitle: 'COMMAND CENTER - Web Application',
      headingSubtitle: 'Operate',
      headingDescription: 'Near Real-Time Fleet Monitoring Dashboard ',
      contentIntroductionText:
        'An enterprise web application that unifies field survey data and live sensor telemetry into a single operational view, giving every stakeholder the right level of visibility to act decisively.',
      contentSubtitle: 'Single pane of glass',
      contentResultText:
        'Continuous fleet visibility that enables early risk detection, intelligent prioritization, and proactive maintenance planning - replacing guesswork with data-driven decision-making across the entire Fortify network.',
      contentMedia: {
        src: mediaPathCreator('solution-overview-media-3-v1.mp4'),
        type: 'video',
        side: 'left',
        loop: true,
      },
      textWithIconCards: [
        {
          heading: 'Executive',
          text: 'Fleet-wide performance trends, KPIs, and strategic reporting',
          iconName: 'chart',
        },
        {
          heading: 'Facility Managers',
          text: 'Site-level operational oversight and alert management',
          iconName: 'facility',
        },
        {
          heading: 'Technicians',
          text: 'Individual dock position status and granular asset detail',
          iconName: 'wrench',
        },
      ],
      contentBulletPoints: [
        'Continuous near real-time monitoring of asset state, usage cycles, and alert conditions',
        'Holistic fleet visualization enabling prioritization',
        'Maintenance management with full asset lifecycle and usage tracking',
        'Fleet-wide reporting and performance analytics across all facilities',
        'Cross-fleet pattern analysis to identify at-risk assets and predict failure before it occurs',
        'Proactive maintenance workflow engine, shifting operations from reactive to planned intervention',
        'Hierarchical, role-based access control for enterprise-grade security',
      ],
      textWithIconHeading: 'Role-Based Views',
    },
  },
  outcomeSectionProperties: {
    sectionTitle: 'A National IoT Platform Built for Scale',
    media: {
      src: mediaPathCreator('outcome-media-v1.png'),
      type: 'image',
      side: 'right',
    },
    paragraphs: [
      'Fortify is positioned to become the definitive authority on dock equipment data across the United States, owning a unified, standardized dataset spanning thousands of facilities.',
      'By combining structured field surveys with continuous sensor telemetry, the platform delivers near real-time asset intelligence that was previously impossible to achieve at this scale. Teams now have consistent visibility into assets that were entirely unmonitored before, understanding condition, usage, and risk within one connected system rather than isolated data.',
      "As the platform continues to scale, Fortify's data advantage compounds, building a living, evolving picture of dock equipment performance across the industry. This positions Fortify not just as a service provider, but as the foundational intelligence layer for the future of warehouse operations.",
    ],
  },
});
